import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dataApiClient } from '../../infrastructure/dataApi/client';
import type { JapanSimDataMeta, SimDataMeta } from '../../types/domain';
import type { Period } from '../../types/common';
import { getPeriod, isJapanSimData } from '../../utils/SimViewer/simdata';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';

type UseMetaData = {
  isLoading: boolean;
  currentDataMeta: SimDataMeta | null;
  allDataMetas: Map<string, SimDataMeta>;
  customDataMetas: Map<string, SimDataMeta>;
  japanDataMetas: Map<string, JapanSimDataMeta>;
  availablePeriods: Map<string, Period>;
};

const EMPTY_DATA = new Map<string, never>();

const useMetaData = (): UseMetaData => {
  const { currentSimDataId } = useCurrentSimDataId();

  const { data, isFetching } = useQuery({
    queryKey: ['meta'],
    queryFn: async (): Promise<SimDataMeta[]> => {
      return dataApiClient.getSimDataMetaList();
    },
    meta: { dataType: 'essentials' },
  });

  const allDataMetas = useMemo((): Map<string, SimDataMeta> | undefined => {
    return data
      ? new Map(
          data.map((dataMeta: SimDataMeta): [string, SimDataMeta] => {
            return [dataMeta.simdata_id, dataMeta];
          })
        )
      : undefined;
  }, [data]);

  const customDataMetas = useMemo((): Map<string, SimDataMeta> | undefined => {
    return data
      ? new Map(
          data
            .filter((simData: SimDataMeta): boolean => {
              return !isJapanSimData(simData.simdata_id);
            })
            .toSorted((data1: SimDataMeta, data2: SimDataMeta): number => {
              return data1.name.localeCompare(data2.name, 'ja');
            })
            .map((simData: SimDataMeta): [string, SimDataMeta] => {
              return [simData.simdata_id, simData];
            })
        )
      : undefined;
  }, [data]);

  const japanDataMetas = useMemo(():
    Map<string, JapanSimDataMeta> | undefined => {
    return data
      ? new Map(
          data
            .filter((simData: SimDataMeta): boolean => {
              return isJapanSimData(simData.simdata_id);
            })
            .map((simData: SimDataMeta): JapanSimDataMeta => {
              const result = getPeriod(simData.simdata_id);
              if (!result) {
                throw new Error(
                  `Invalid Japan simdata_id: ${simData.simdata_id}`
                );
              }
              return {
                ...simData,
                prefectureCode: result.prefectureCode,
                year: result.year,
                month: result.month,
                dayType: result.dayType,
                version: result.version,
              };
            })
            .toSorted(
              (data1: JapanSimDataMeta, data2: JapanSimDataMeta): number => {
                // 都道府県・年・月・曜日種別・バージョンの優先度で昇順に並び替える
                return `${data1.prefectureCode}_${data1.year}_${data1.month}_${data1.dayType}_${data1.version}`.localeCompare(
                  `${data2.prefectureCode}_${data2.year}_${data2.month}_${data2.dayType}_${data2.version}`,
                  'en'
                );
              }
            )
            .filter(
              (
                data1: JapanSimDataMeta,
                _,
                allList: JapanSimDataMeta[]
              ): boolean => {
                // 同じデータのうち、バージョンが一番大きいものだけ残す
                const versionList = allList
                  .filter((data2: JapanSimDataMeta): boolean => {
                    return (
                      data2.prefectureCode === data1.prefectureCode &&
                      data2.year === data1.year &&
                      data2.month === data1.month &&
                      data2.dayType === data1.dayType
                    );
                  })
                  .map((data2: JapanSimDataMeta): number => {
                    return data2.version;
                  });
                return data1.version === Math.max(...versionList);
              }
            )
            .map((data: JapanSimDataMeta): [string, JapanSimDataMeta] => {
              return [data.simdata_id, data];
            })
        )
      : undefined;
  }, [data]);

  const availablePeriods = useMemo((): Map<string, Period> | undefined => {
    return japanDataMetas
      ? new Map<string, Period>(
          Array.from(japanDataMetas.values()).map(
            (dataMeta: JapanSimDataMeta): [string, Period] => {
              const key = `${dataMeta.year}-${dataMeta.month}-${dataMeta.dayType}`;
              return [
                key,
                {
                  key,
                  year: dataMeta.year,
                  month: dataMeta.month,
                  dayType: dataMeta.dayType,
                },
              ];
            }
          )
        )
      : undefined;
  }, [japanDataMetas]);

  return {
    isLoading: isFetching,
    currentDataMeta: currentSimDataId
      ? (allDataMetas?.get(currentSimDataId) ?? null)
      : null,
    allDataMetas: allDataMetas ?? EMPTY_DATA,
    customDataMetas: customDataMetas ?? EMPTY_DATA,
    japanDataMetas: japanDataMetas ?? EMPTY_DATA,
    availablePeriods: availablePeriods ?? EMPTY_DATA,
  };
};

export { useMetaData };
